<template>
    <section class="admin-creator">
  
      <div class="creator-header">
  
        <div>
          <h3>
            {{ isEditing ? "Edit Admin" : "Add Admin" }}
          </h3>
  
          <p>
            {{ isEditing ? "Update this administrator's profile and access." : "Create a new admin access request." }}
          </p>
        </div>
  
      </div>
  
      <form
        class="creator-form"
        @submit.prevent="submitAdmin"
      >
  
        <label>
          Name
  
          <input
            v-model="name"
            type="text"
            autocomplete="name"
          />
        </label>
  
        <label>
          Email
  
          <input
            v-model="email"
            type="email"
            autocomplete="email"
          />
        </label>
  
        <fieldset class="access-panel">

          <legend>
            Access Levels
          </legend>

          <p class="access-help">
            Select the areas this administrator should be able to use.
          </p>

          <div class="role-grid">

            <label
              v-for="role in availableRoles"
              :key="role.value"
              class="role-option"
              :class="{ selected: roles.includes(role.value) }"
            >
              <input
                v-model="roles"
                type="checkbox"
                :value="role.value"
              />

              <span class="role-indicator" aria-hidden="true"></span>

              <span class="role-copy">
                <span class="role-title">
                  {{ role.label }}
                </span>

                <span class="role-description">
                  {{ role.description }}
                </span>
              </span>
            </label>

          </div>

        </fieldset>
  
        <AdminAuthHistory v-if="isEditing && canManageAdmins" :key="admin.adminId || admin.id" :uid="admin.adminId || admin.id" />

        <div class="creator-actions">

            <button
            type="button"
            class="cancel-button"
            @click="$emit('cancel')"
            >
            Cancel
            </button>

            <button
            type="submit"
            class="submit-button"
            :disabled="saving || !isValid || !canManageAdmins"
            >
            {{ saving ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Admin" : "Create Admin") }}
            </button>

            </div>
  
        <p
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </p>
  
      </form>
  
    </section>
  </template>
  
  <script>
  import AdminAuthHistory from "./AdminAuthHistory.vue";
  import {
    computed,
    ref,
    watch
  } from "vue";
  
  import {
    saveAdminToFirestore,
    updateAdminAccount
  } from "@/firebase/firestore";
  
  export default {
    components: { AdminAuthHistory },
    name:
      "AdminCreator",

    props: {
      admin: {
        type: Object,
        default: null
      },
      canManageAdmins: {
        type: Boolean,
        default: false
      }
    },
  
    emits: [
      "cancel",
      "saved"
    ],
  
    setup(
      props,
      {
        emit
      }
    ) {
  
      const name =
        ref("");
  
      const email =
        ref("");
  
      const roles =
        ref([]);
  
      const saving =
        ref(false);
  
      const errorMessage =
        ref("");

      const initialSnapshot =
        ref("");
  
      const availableRoles = [
        {
          value: "admin",
          label: "Admin",
          description: "Administrator tools and account management."
        },
        {
          value: "data",
          label: "Data",
          description: "Dashboard data and reporting tools."
        },
        {
          value: "lawyers",
          label: "Lawyers",
          description: "Attorney records and request management."
        },
        {
          value: "litigations",
          label: "Litigations",
          description: "Litigation records and management tools."
        },
        {
          value: "development",
          label: "Development",
          description: "Development and diagnostic tools."
        }
      ];

      const allowedRoleValues =
        availableRoles.map(role => role.value);

      const normalizeRoles =
        values => {

          const selected =
            Array.isArray(values) ? values : [];

          return allowedRoleValues.filter(role => selected.includes(role));
        };

      const formSnapshot =
        () => JSON.stringify({
          name: name.value.trim(),
          email: email.value.trim().toLowerCase(),
          roles: normalizeRoles(roles.value)
        });

      const isEditing =
        computed(() => Boolean(props.admin?.adminId || props.admin?.id));

      const hasChanges =
        computed(() => formSnapshot() !== initialSnapshot.value);

      watch(
        () => props.admin,
        adminRecord => {

          name.value =
            adminRecord?.name ?? "";

          email.value =
            adminRecord?.email ?? "";

          roles.value =
            normalizeRoles(adminRecord?.type);

          errorMessage.value =
            "";

          initialSnapshot.value =
            formSnapshot();
        },
        { immediate: true }
      );
  
      const isValid =
        computed(() => {
  
          return (
            name.value.trim() !==
              "" &&
            email.value.trim() !==
              "" &&
            roles.value.length >
              0 &&
            (!isEditing.value || hasChanges.value)
          );
        });

      const adminSaveErrorMessage =
        (
          error,
          editing
        ) => {

          switch (error?.code) {
          case "functions/unauthenticated":
            return "Your session has expired. Please sign in again.";

          case "functions/permission-denied":
            return "You do not have permission to manage administrators.";

          case "functions/already-exists":
          case "functions/invalid-argument":
          case "functions/failed-precondition":
          case "functions/not-found":
            return error.message;

          default:
            return editing
              ? "Unable to save the administrator changes."
              : "Unable to create the admin request.";
          }
        };
  
      const submitAdmin =
        async () => {
  
          if (
            !isValid.value ||
            saving.value ||
            !props.canManageAdmins
          ) {
  
            return;
          }
  
          saving.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            const adminData = {
              name: name.value.trim(),
              email: email.value.trim().toLowerCase(),
              roles: normalizeRoles(roles.value)
            };

            const saved = isEditing.value
              ? await updateAdminAccount({
                  adminId: props.admin.adminId ?? props.admin.id,
                  ...adminData
                })
              : await saveAdminToFirestore(adminData);
  
            if (!saved) {
  
              throw new Error(
                isEditing.value
                  ? "Admin changes were not saved."
                  : "Admin request was not saved."
              );
            }
  
            emit(
              "saved"
            );
  
          } catch (error) {
  
            console.error(
              isEditing.value
                ? "❌ Unable to update admin:"
                : "❌ Unable to create admin:",
              error
            );
  
            errorMessage.value =
              adminSaveErrorMessage(
                error,
                isEditing.value
              );
  
          } finally {
  
            saving.value =
              false;
          }
        };
  
      return {
        name,
        email,
        roles,
        saving,
        errorMessage,
        availableRoles,
        isEditing,
        hasChanges,
        isValid,
        submitAdmin
      };
    }
  };
  </script>

<style scoped>

.admin-creator {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.creator-header h3 {
  margin: 0;
  color: #1c2923;
  font-size: 1.15rem;
}

.creator-header p {
  margin: 5px 0 0;
  color: #747e79;
}

.creator-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.creator-form > label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #34423b;
  font-size: 0.88rem;
  font-weight: 700;
}

.creator-form > label input {
  min-height: 46px;
  padding: 0 14px;
  border: 1px solid #d7ded9;
  border-radius: 10px;
  background: #ffffff;
  color: #1c2923;
  font: inherit;
  font-weight: 400;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.creator-form > label input:focus {
  border-color: #18ab69;
  box-shadow: 0 0 0 3px rgba(24, 171, 105, 0.12);
}

.access-panel {
  margin: 4px 0 0;
  padding: 20px;
  border: 1px solid #dce4df;
  border-radius: 14px;
  background: #f5f6f5;
}

.access-panel legend {
  padding: 0 8px;
  color: #1c2923;
  font-size: 0.95rem;
  font-weight: 750;
}

.access-help {
  margin: 0 0 16px;
  color: #747e79;
  font-size: 0.84rem;
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.role-option {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: 72px;
  padding: 14px;
  border: 1px solid #dce4df;
  border-radius: 11px;
  background: #f5f5f5;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.role-option:hover {
  border-color: #a9cdbb;
  transform: translateY(-1px);
}

.role-option.selected {
  border-color: #18ab69;
  box-shadow: 0 0 0 2px rgba(24, 171, 105, 0.1);
}

.role-option input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.role-option:focus-within {
  outline: 3px solid rgba(24, 171, 105, 0.16);
  outline-offset: 2px;
}

.role-indicator {
  flex: 0 0 auto;
  width: 19px;
  height: 19px;
  margin-top: 1px;
  border: 1.5px solid #aeb9b3;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: inset 0 0 0 4px #ffffff;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.role-option.selected .role-indicator {
  border-color: #18ab69;
  background: #18ab69;
}

.role-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.role-title {
  color: #25322c;
  font-size: 0.9rem;
  font-weight: 750;
}

.role-description {
  color: #75807a;
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.35;
}

.creator-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
}

.cancel-button,
.submit-button {
  min-width: 140px;
  padding: 12px 18px;
  border: none;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}

.cancel-button {
  background: #d64545;
  color: white;
}

.cancel-button:hover {
  background: #be3838;
}

.submit-button {
  background: #18ab69;
  color: white;
}

.submit-button:hover:not(:disabled) {
  background: #15945c;
}

.submit-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.error-message {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #efc5c5;
  border-radius: 10px;
  background: #fff7f7;
  color: #a33636;
}

@media (max-width: 700px) {
  .role-grid {
    grid-template-columns: 1fr;
  }

  .creator-actions {
    flex-direction: column-reverse;
  }

  .cancel-button,
  .submit-button {
    width: 100%;
  }
}
</style>
